import React, { useState, useEffect, useContext, useMemo } from 'react';
import client from '@js/modules/httpClient/index';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import {CategoryAnswerType} from '@js/components/corporateRegistrations/CategoryAnswerType';
import { CompatibilityFormData } from '@js/interfaces/index';
import { usePath } from '@js/hooks/usePath';
import { Context } from '@js/routes';
import { InvestorRegistrationContainer } from '../common/investorRegistrations/InvestorRegistrationContainer';
import { CONFIRM_MESSAGE } from '@js/components/corporateRegistrations/constants';
import Toast from '@js/modules/toast';

type BlockingOption = {
  option_id: number;
  blocks_registration: boolean;
  alert_message: string;
};

type QuestionItem = {
  category_id: number,
  category_name: string,
  category_answer_type: 'single' | 'multiple' | 'text',
  option_ids: number[],
  option_names: string[],
  existing_value: number | null,
  blocking_options?: BlockingOption[]
};

export const CorporateCompatibility = () => {
  const { setIsLoading, isEdit, setIsEdit, setShouldSkipCompatibility } = useContext(Context);
  const [question_list, set_question_list] = useState<QuestionItem[]>([]);
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CompatibilityFormData>({
    shouldUnregister: false
  });
  const { currentStep, pushNext } = usePath(isEdit);


  const onSubmit = (data: CompatibilityFormData) => {
    const keys = Object.keys(data) as (keyof CompatibilityFormData)[];
    const submitData = { investor_compatibilities:
      keys.filter(key => key.match(/category_option_(\d+)/))
        .map(key => ({
          category_id: key.replace('category_option_', ''),
          value: data[key]
        }))
    };
    if(isEdit && !confirm(CONFIRM_MESSAGE)) return;
    client.post('/api/v1/customer/investor_registration/investor_compatibility', submitData)
      .then((response) => {
        if (isEdit) {
          const message = response.data.message;
          Toast.setFlashNotice(message);
        }
        pushNext();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    client.get('/api/v1/customer/investor_registration/investor_compatibility')
      .then(response => {
        const data = response.data;
        set_question_list(data.investor_compatibilities);
        setIsEdit(!data['registration_in_progress?']);

        // 投資適合性カテゴリが公開されていない場合、スキップフラグを設定（新規登録モードのみ）
        if (data['registration_in_progress?']) {
          if (data.investor_compatibilities.length === 0) {
            setShouldSkipCompatibility(true);
            // 自動的に次のページに遷移
            pushNext();
          } else {
            setShouldSkipCompatibility(false);
          }
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line
  useEffect(() => {
    question_list.forEach((question) => {
      if(!question.existing_value && question.existing_value !== 0) return;
      setValue(`category_option_${question.category_id}`, `${question.existing_value}`);
    });
    setIsLoading(false);
  }, [question_list, setValue, setIsLoading]);

  // ブロック判定: フォーム値から派生状態として計算
  const watchedValues = watch();
  const blockingAlerts = useMemo(() => {
    const alerts: { [key: number]: string | null } = {};

    question_list.forEach((questionItem) => {
      // 単一回答かつブロックオプションがある場合のみチェック
      if (questionItem.category_answer_type !== 'single' || !questionItem.blocking_options) {
        alerts[questionItem.category_id] = null;
        return;
      }

      const selectedValue = watchedValues[`category_option_${questionItem.category_id}`];
      const selectedOptionId = selectedValue ? parseInt(selectedValue as string, 10) : null;
      const blockingOption = questionItem.blocking_options.find(
        opt => opt.option_id === selectedOptionId && opt.blocks_registration
      );

      alerts[questionItem.category_id] = blockingOption ? blockingOption.alert_message : null;
    });

    return alerts;
  }, [watchedValues, question_list]);

  const isBlocked = Object.values(blockingAlerts).some(alert => alert !== null && alert !== undefined);

  return (
    <InvestorRegistrationContainer formId={currentStep.name} isEdit={isEdit} isBlocked={isBlocked} >
      <form onSubmit={handleSubmit(onSubmit)} className="form-wrap mt40" id={currentStep.name}>
        {question_list.map((questionItem) => {
          if(questionItem.category_answer_type === 'text') {
            return (
              <div className="form-input__wrap" key={questionItem.category_id}>
                <label className="form-label" htmlFor={`category_option_${questionItem.category_id}`} >
                  <span className="form-label__required">必須</span>
                  { questionItem.category_name }
                </label>
                <CategoryAnswerType questionItem={questionItem} register={register} errors={errors} />
                <ErrorMessage
                  errors={errors}
                  name={`category_option_${questionItem.category_id}`}
                  render={({ message }) =>
                    <span className="form-input__error">{message}</span>}
                />
              </div>
            );
          }
          return (
            <fieldset className="form-input__wrap" key={questionItem.category_id}>
              <legend className="form-label">
                <span className="form-label__required">必須</span>
                { questionItem.category_name }
              </legend>
              <CategoryAnswerType questionItem={questionItem} register={register} errors={errors} />
              {blockingAlerts[questionItem.category_id] && (
                <span className="form-input__error" style={{ fontSize: '14px' }}>
                  {blockingAlerts[questionItem.category_id]}
                </span>
              )}
              <ErrorMessage
                errors={errors}
                name={`category_option_${questionItem.category_id}`}
                render={({ message }) =>
                  <span className="form-input__error">{message}</span>}
              />
            </fieldset>
          );
        })}
      </form>
    </InvestorRegistrationContainer>
  );
};
